import React from 'react';
import { User } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/integrated-auth';
import { Avatar, AvatarImage, AvatarFallback } from './avatar';

interface UserAvatarProps {
  userId?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  onClick?: () => void;
  className?: string;
}

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-16 w-16",
  xl: "h-24 w-24",
  "2xl": "h-32 w-32",
  "3xl": "h-40 w-40 sm:h-48 sm:w-48 lg:h-56 lg:w-56"
};

const iconSizes = {
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
  "2xl": 48,
  "3xl": 64
};

export function UserAvatar({ userId, size = "md", onClick, className = "" }: UserAvatarProps) {
  // If no userId provided, get current user from auth context (avoids duplicate /api/user fetch)
  const { user: authUser } = useAuth();

  // If userId provided, get that specific user
  const { data: specificUser } = useQuery({
    queryKey: [`/api/users/${userId}`],
    enabled: !!userId,
    staleTime: 30000,
  });

  const targetUser = userId ? specificUser : authUser;
  const avatarUrl = targetUser?.avatarImageUrl;

  return (
    <Avatar
      className={`
        ${sizeClasses[size]}
        ${onClick ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}
        ${className}
      `}
      onClick={onClick}
    >
      <AvatarImage
        src={avatarUrl || undefined}
        alt="Avatar"
      />
      <AvatarFallback>
        <User size={iconSizes[size]} />
      </AvatarFallback>
    </Avatar>
  );
}
